/**
 * שעות — the timer and the log behind it.
 *
 * Starting a timer stops whichever one was already running, rather than refusing: a
 * bookkeeper switching client mid-morning should not have to remember to stop the old
 * one, and what happens otherwise in practice is an entry that runs until they notice.
 * The database enforces the single-running rule with a partial unique index, so this is
 * a convenience over a guarantee rather than the guarantee itself.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDate, formatDateTime } from "@onecpa/shared";
import {
  Button,
  type Column,
  DataTable,
  EmptyState,
  ErrorState,
  Field,
  Input,
  KpiStrip,
  PageHeader,
  Panel,
  Select,
} from "@onecpa/ui";
import { api, type TimeEntryRow } from "../api/client.js";
import { minutesToLabel } from "../app/hooks.js";
import { useRole, useSession } from "../app/session.js";

export function TimeSheet() {
  const session = useSession();
  const { isManager } = useRole();
  const queryClient = useQueryClient();

  const monthStart = new Date();
  monthStart.setUTCDate(1);
  const [from, setFrom] = useState(monthStart.toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [clientId, setClientId] = useState("");
  const [description, setDescription] = useState("");

  const { data: clients } = useQuery({
    queryKey: ["clients", session.orgId, "", "active"],
    queryFn: () => api.clients.list(session.orgId, { status: "active", limit: 200 }),
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["time", session.orgId, from, to],
    queryFn: () => api.time.list(session.orgId, { from, to }),
  });

  const { data: running } = useQuery({
    queryKey: ["time", "running", session.orgId],
    queryFn: () => api.time.running(session.orgId),
  });

  const start = useMutation({
    mutationFn: () =>
      api.time.start(session.orgId, {
        ...(clientId ? { clientId } : {}),
        ...(description.trim() ? { description: description.trim() } : {}),
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["time"] });
      session.toast(result.running ? "הטיימר הופעל" : "הטיימר הופעל");
      setDescription("");
    },
    onError: (mutationError: Error) => session.toast(mutationError.message, "danger"),
  });

  const stop = useMutation({
    mutationFn: () => api.time.stop(session.orgId),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["time"] });
      session.toast(`נרשמו ${minutesToLabel(result.entry.minutes)}`);
    },
    onError: (mutationError: Error) => session.toast(mutationError.message, "danger"),
  });

  const rows = data?.entries ?? [];
  const billable = rows
    .filter((row) => row.isBillable)
    .reduce((sum, row) => sum + (row.minutes ?? 0), 0);

  const columns: Column<TimeEntryRow>[] = [
    {
      key: "date",
      header: "תאריך",
      sticky: true,
      width: "130px",
      cell: (row) => <bdi className="tnum text-xs text-ink">{formatDate(row.startedAt)}</bdi>,
    },
    {
      key: "client",
      header: "לקוח",
      cell: (row) => <span className="text-xs text-ink">{row.clientName ?? "כללי"}</span>,
    },
    {
      key: "description",
      header: "תיאור",
      cell: (row) => <span className="text-xs text-ink-muted">{row.description ?? "—"}</span>,
    },
    ...(isManager
      ? [
          {
            key: "user",
            header: "עובד",
            hideOnSmall: true,
            cell: (row: TimeEntryRow) => (
              <span className="text-xs text-ink-muted">{row.userName ?? "—"}</span>
            ),
          },
        ]
      : []),
    {
      key: "minutes",
      header: "משך",
      align: "end",
      cell: (row) => (
        <bdi className="tnum text-xs font-semibold text-ink">
          {row.minutes === null ? "פועל" : minutesToLabel(row.minutes)}
        </bdi>
      ),
    },
  ];

  if (error) {
    return <ErrorState message={(error as Error).message} onRetry={() => void refetch()} />;
  }

  return (
    <>
      <PageHeader title="שעות" subtitle={isManager ? "כל העובדים" : "השעות שלי"} />

      <KpiStrip
        items={[
          {
            label: "סה״כ בטווח",
            value: data ? minutesToLabel(data.totalMinutes) : undefined,
            tone: "ledger",
          },
          { label: "לחיוב", value: data ? minutesToLabel(billable) : undefined },
          { label: "רשומות", value: data ? rows.length : undefined },
          {
            label: "טיימר",
            value: running ? (running.running ? "פועל" : "כבוי") : undefined,
            tone: running?.running ? "ledger" : "ink",
          },
          {
            label: "ממוצע לרשומה",
            value: !data
              ? undefined
              : rows.length > 0
                ? minutesToLabel(Math.round(data.totalMinutes / rows.length))
                : "—",
          },
        ]}
      />

      <div className="grid gap-4 p-5 lg:grid-cols-3">
        <Panel title="טיימר" className="lg:col-span-1">
          <div className="flex flex-col gap-3 px-3.5 py-3">
            {running?.running ? (
              <>
                <p className="text-xs text-ink-muted">
                  פועל מאז <bdi className="tnum">{formatDateTime(running.running.startedAt)}</bdi>
                  {running.running.clientName ? ` · ${running.running.clientName}` : ""}
                </p>
                <Button variant="primary" onClick={() => stop.mutate()} loading={stop.isPending}>
                  עצירת הטיימר
                </Button>
              </>
            ) : (
              <>
                <Field label="לקוח">
                  {({ id }) => (
                    <Select
                      id={id}
                      value={clientId}
                      onChange={(event) => setClientId(event.target.value)}
                    >
                      <option value="">כללי</option>
                      {(clients?.clients ?? []).map((client) => (
                        <option key={client.id} value={client.id}>
                          {client.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field label="תיאור">
                  {({ id }) => (
                    <Input
                      id={id}
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                      placeholder="לדוגמה: התאמות בנקים"
                    />
                  )}
                </Field>
                <Button
                  variant="primary"
                  icon="timer"
                  onClick={() => start.mutate()}
                  loading={start.isPending}
                >
                  התחלת טיימר
                </Button>
                <p className="text-2xs text-ink-faint">
                  טיימר נעצר אוטומטית לאחר 8 שעות, כדי שטיימר שנשכח לא יחייב לקוח על לילה שלם.
                </p>
              </>
            )}
          </div>
        </Panel>

        <div className="lg:col-span-2">
          <div className="mb-3 flex flex-wrap items-end gap-3">
            <Field label="מתאריך" className="w-40">
              {({ id }) => (
                <Input
                  id={id}
                  type="date"
                  value={from}
                  onChange={(event) => setFrom(event.target.value)}
                />
              )}
            </Field>
            <Field label="עד תאריך" className="w-40">
              {({ id }) => (
                <Input
                  id={id}
                  type="date"
                  value={to}
                  onChange={(event) => setTo(event.target.value)}
                />
              )}
            </Field>
          </div>

          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(row) => row.id}
            loading={isLoading}
            density="compact"
            caption="רשומות שעות"
            empty={<EmptyState title="אין רשומות בטווח" icon="timer" />}
          />
        </div>
      </div>
    </>
  );
}
